/**
 * Client-side scheduling utilities for filtering and processing time slots.
 * These functions are shared between the UI and command-line scripts.
 */

import moment from 'moment'
import { debug } from './logging'
import { DaySlotType, TimeSlotType } from './scheduling'
import { AppointmentTypeType } from 'app/models/AppointmentType'
import { AvailabilityType } from 'app/models/Availability'
import { ProviderType } from 'app/models/Provider'
import { SchedulingConfigurationJson } from 'app/models/SchedulingConfiguration'

/**
 * Calculate the difference in minutes between two time strings (e.g., "10:00 AM", "11:00 AM")
 */
function getTimeDifferenceInMinutes(startTime: string, endTime: string): number {
  const start = moment(startTime, `h:mm A`)
  const end = moment(endTime, `h:mm A`)
  return end.diff(start, `minutes`)
}

/**
 * Filters day slots by appointment type and provider, with duration validation.
 *
 * This function ensures that:
 * 1. Only slots supporting the appointment type's vendor ID are included
 * 2. Only slots with the specified provider are included (if providerId specified)
 * 3. Slots have enough consecutive time for the appointment duration
 *
 * @param daySlots - Array of day slots to filter
 * @param appointmentType - The appointment type to filter by
 * @param schedulingConfiguration - Configuration including granularity settings
 * @param providerId - Optional provider ID to filter by
 * @returns Filtered day slots with only valid time slots
 */
export function filterSlotsByAppointmentType(
  daySlots: DaySlotType[],
  appointmentType: AppointmentTypeType,
  schedulingConfiguration: SchedulingConfigurationJson,
  providerId?: string,
): DaySlotType[] {
  const granularity = schedulingConfiguration?.granularity || 60
  const appointmentDuration = appointmentType.appointmentLength || 60
  const slotsNeeded = Math.ceil(appointmentDuration / granularity)

  return daySlots
    .filter((daySlot) => daySlot != null)
    .map((daySlot) => {
      const allTimeSlots = daySlot.timeSlots || []

      return {
        ...daySlot,
        timeSlots: allTimeSlots
          .map((timeSlot) => {
            // Filter availabilities by appointment type vendor ID
            let filteredAvailabilities = timeSlot.availabilities.filter((avail) =>
              avail.appointmentTypeIds?.includes(appointmentType.vendorId!),
            )

            // Further filter by provider if specified
            if (providerId) {
              filteredAvailabilities = filteredAvailabilities.filter((avail) => avail.providerId === providerId)
            }

            // Duration validation: For multi-slot appointments, only keep availabilities with enough consecutive time
            if (slotsNeeded > 1) {
              filteredAvailabilities = filteredAvailabilities.filter((avail) => {
                const currentSlotIndex = allTimeSlots.indexOf(timeSlot)
                debug(
                  `scheduling`,
                  `filterSlotsByAppointmentType: Checking duration for ${avail.providerId} at ${timeSlot.time}`,
                  {
                    providerId: avail.providerId,
                    operatoryId: avail.operatoryId,
                    time: timeSlot.time,
                    slotsNeeded,
                    currentSlotIndex,
                  },
                )

                // Check if this specific provider+operatory combo has enough consecutive slots
                for (let i = 0; i < slotsNeeded; i++) {
                  const futureSlotIndex = currentSlotIndex + i
                  const futureSlot = allTimeSlots[futureSlotIndex]
                  if (!futureSlot) {
                    debug(`scheduling`, `filterSlotsByAppointmentType: No future slot at index ${futureSlotIndex}`)
                    return false
                  }

                  // Verify the future slot is actually consecutive in time (not just in array position)
                  if (i > 0) {
                    const expectedMinutesDiff = granularity * i
                    const actualMinutesDiff = getTimeDifferenceInMinutes(timeSlot.time, futureSlot.time)

                    debug(`scheduling`, `filterSlotsByAppointmentType: Time gap check`, {
                      futureSlotTime: futureSlot.time,
                      expectedMinutesDiff,
                      actualMinutesDiff,
                    })

                    // If the time gap doesn't match the expected duration, slots aren't consecutive
                    if (actualMinutesDiff !== expectedMinutesDiff) {
                      debug(`scheduling`, `filterSlotsByAppointmentType: Time gap mismatch, filtering out`)
                      return false
                    }
                  }

                  // Check if the future slot has the same provider+operatory combo that supports this appointment type
                  const hasSameAvailability = futureSlot.availabilities.some(
                    (futureAvail) =>
                      futureAvail.providerId === avail.providerId &&
                      futureAvail.operatoryId === avail.operatoryId &&
                      futureAvail.appointmentTypeIds?.includes(appointmentType.vendorId!),
                  )

                  debug(`scheduling`, `filterSlotsByAppointmentType: Future slot availability check`, {
                    futureSlotTime: futureSlot.time,
                    futureSlotAvailabilities: futureSlot.availabilities.length,
                    hasSameAvailability,
                  })

                  if (!hasSameAvailability) {
                    debug(
                      `scheduling`,
                      `filterSlotsByAppointmentType: No matching availability in future slot, filtering out`,
                    )
                    return false
                  }
                }
                debug(`scheduling`, `filterSlotsByAppointmentType: Passed all checks, keeping availability`)
                return true
              })
            }

            return {
              ...timeSlot,
              availabilities: filteredAvailabilities,
            }
          })
          .filter((timeSlot) => timeSlot.availabilities.length > 0),
      }
    })
}

/**
 * Filters time slots by appointment type vendor ID only (no duration validation).
 * Used for simple filtering scenarios where duration is not a concern.
 *
 * @param timeSlots - Array of time slots to filter
 * @param appointmentTypeVendorId - The vendor ID to filter by
 * @param providerId - Optional provider ID to filter by
 * @returns Filtered time slots
 */
export function filterTimeSlotsByVendorId(
  timeSlots: TimeSlotType[],
  appointmentTypeVendorId: string,
  providerId?: string,
): TimeSlotType[] {
  return timeSlots
    .map((timeSlot) => ({
      ...timeSlot,
      availabilities: timeSlot.availabilities.filter(
        (avail) =>
          avail.appointmentTypeIds?.includes(appointmentTypeVendorId) &&
          (!providerId || avail.providerId === providerId),
      ),
    }))
    .filter((timeSlot) => timeSlot.availabilities.length > 0)
}

/**
 * Filters providers based on appointment type availability.
 * Returns only providers that have at least one availability supporting the appointment type.
 *
 * @param providers - All available providers
 * @param availabilities - All availabilities
 * @param appointmentType - The appointment type to filter by
 * @returns Providers that support the appointment type
 */
export function filterProvidersByAppointmentType(
  providers: ProviderType[],
  availabilities: AvailabilityType[],
  appointmentType: AppointmentTypeType,
): ProviderType[] {
  const relevantAvailabilities = availabilities.filter(
    (a) => a.appointmentTypeIds?.includes(appointmentType.vendorId!) && providers.find((p) => p.id === a.providerId),
  )
  return providers.filter((p) => relevantAvailabilities.some((a) => a.providerId === p.id))
}
